// OnboardingScreen.tsx — 솟다 가입 직후 온보딩 가이드 (통합 5스텝 + 첫 10연 선물)
// design doc: 2026-06-08-sotda-coldstart-retention §3·§7 (효신님 "통합 확장" 결정)
//   스텝1 황둥이 인사(기존 살림 + 까미 cameo) → 2 활동BULL 환산 → 3 모의투자 제안 → 4 실패박물관 티저
//   claude.ai/design 채택안 영입(cs-tokens 재구현). CTA "모의투자 시작" → /sim.
//   ⚠ 기존 "자산 기록하기" 온보딩은 자산연동 탭 첫 진입으로 분리(효신님 2026-06-08).
// 탭바 없는 풀스크린(가입 플로우) — OnboardingPage가 AppLayout만 제공.
// @ts-nocheck
import '../cs-tokens.css';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BullCoin, CSBtn, CSIcon } from '../_components/cs';
import { CastAvatar, CastPortrait } from '../_components/cast';
import { useAuth } from '../../lib/useAuth';
import { markOnboarded } from '../../lib/onboarding';
import { claimAndToast } from '../../ui/achievementEffects';
import { apiFetch } from '../../lib/api';

// FM 골드-온-다크 (cs-tokens 미정의 영역 — 실패박물관 티저 한정 인라인)
const FM_INK_DEEP = 'var(--cs-museum-ink)';
const FM_GOLD = 'var(--cs-gold-on-ink)';

export function SotdaOnboardingScreen() {
  const nav = useNavigate();
  const { user } = useAuth();
  const nick = user?.nickname || user?.name || ''; // 환영 문구 보간 (없으면 '새 투자자' 폴백)
  // ⚠ 2026-08-02: 노출 즉시 '봤다'를 기록한다 — 기존엔 완주/건너뛰기(claimGift 성공) 시에만
  //   저장돼, 버튼 없이 이탈(하드웨어 뒤로가기·앱 종료)하거나 선물 API가 실패하면 플래그가
  //   영영 안 남아 매 로그인마다 재노출됐다. 정본 의도는 "1회 노출"(onboarding.ts 주석)이고
  //   선물 지급은 서버 멱등(grant_draw_ticket UNIQUE)이 별도로 보호하므로 분리해도 안전하다.
  // 2026-09-15: 첫걸음(first_step = 가입 완료) — 모바일엔 claim 호출이 없어 모바일 가입자 전원이 못 받았다.
  //   가입 직후 1회 노출되는 이 화면이 모바일의 "가입 완료" 시점이다. 서버 멱등·동시호출 묶음(claimAndToast).
  useEffect(() => {
    if (!user?.id) return;
    void markOnboarded(user.id);
    void claimAndToast('onboarding');
  }, [user?.id]);
  const [step, setStep] = useState(0); // 0~4
  const [giftBusy, setGiftBusy] = useState(false);
  const [giftError, setGiftError] = useState('');
  const [alreadyClaimed, setAlreadyClaimed] = useState(false); // 서버가 already_granted를 돌려준 계정
  const last = step === 4;

  // 완료·건너뛰기 모두 서버 선물을 먼저 확정한다. 로컬 플래그 삭제/앱 재설치와 무관하게
  // grant_draw_ticket의 서버 멱등키가 계정 생애 1회를 보장한다.
  // 이동만 분기: 완료=가챠(첫 10연 연출) / 건너뛰기=홈 (효신님 2026-07-10 — 건너뛴 유저를 가챠로 끌지 않음)
  //
  // ⚠ 2026-07-26: 응답의 granted를 읽는다. RPC는 처음부터 {granted:false, reason:'already_granted'}를
  //   정확히 돌려주고 있었는데 화면이 버려서, 재설치 후 온보딩이 다시 뜨면 "드려요"라고 해놓고
  //   실제 지급은 없는 상태가 됐다. 이미 받은 계정이면 이동하지 않고 사실을 먼저 알린다(1탭 더).
  //   announce=true(마지막 스텝 경로)일 때만 화면에 알린다. 건너뛰기는 스텝5가 보이지 않는 상태라
  //   여기서 멈추면 "버튼을 눌렀는데 아무 일도 없음"이 되므로 조용히 이동시킨다.
  const claimGift = async (dest: string, announce = false) => {
    if (giftBusy) return;
    setGiftBusy(true); setGiftError('');
    try {
      const res = await apiFetch<{ ok: boolean; result?: { granted?: boolean; reason?: string } }>(
        '/api/mobile/onboarding/gift', { method: 'POST' },
      );
      await markOnboarded(user?.id);
      // granted가 명시적으로 false일 때만 안내 — 필드 부재(옛 서버)는 지급으로 간주해 흐름을 막지 않는다
      if (announce && res?.result?.granted === false && !alreadyClaimed) {
        setAlreadyClaimed(true);
        return; // 화면에 사실을 표시하고, 다음 탭에서 이동
      }
      // ⚠ 2026-07-26 뒤로가기 정리: 온보딩은 가입 플로우의 **종착점**이라 여기서 뒤로 가면
      //   로그인 이전(비로그인) 화면으로 돌아가 버렸다. 온보딩 엔트리를 홈으로 replace한 뒤
      //   목적지를 push한다 → 목적지에서 뒤로가기 = 홈. 건너뛰기(dest='/')는 replace 한 번으로 끝.
      nav('/', { replace: true });
      if (dest !== '/') nav(dest);
    } catch {
      setGiftError('선물을 받지 못했어요. 잠시 후 다시 눌러 주세요.');
    } finally {
      setGiftBusy(false);
    }
  };
  const next = () => { if (last) void claimGift('/gacha?first=1', true); else setStep((s) => s + 1); };
  const skip = () => { void claimGift('/'); };

  return (
    <div className="cs-root" data-density="regular" data-temp="warm" data-heading="serif"
      style={{ width: '100%', minHeight: '100%', position: 'relative', background: 'var(--cs-bg)', color: 'var(--cs-ink)', display: 'flex', flexDirection: 'column' }}>

      {/* 진행 인디케이터 (실제 4스텝) */}
      <div style={{ display: 'flex', gap: 4, padding: '14px 20px 0' }}>
        {[0, 1, 2, 3, 4].map((i) => <Step key={i} done={i < step} active={i === step} />)}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 20px 0' }}>
        <span style={{ fontSize: 11.5, color: 'var(--cs-mute)', fontFamily: 'var(--cs-font-mono)', letterSpacing: '0.04em' }}>
          {String(step + 1).padStart(2, '0')} / 05
        </span>
        {!last && (
          <button onClick={skip} style={{ background: 'transparent', border: 0, color: 'var(--cs-mute)', fontSize: 12.5, cursor: 'pointer', fontWeight: 500 }}>건너뛰기</button>
        )}
      </div>

      {/* 스텝 본문 */}
      <div style={{ flex: 1, padding: '20px 24px 0', display: 'flex', flexDirection: 'column' }}>
        {step === 0 && <StepWelcome nick={nick} />}
        {step === 1 && <StepBull />}
        {step === 2 && <StepSim />}
        {step === 3 && <StepMuseum />}
        {step === 4 && <StepGift claimed={alreadyClaimed} />}

        {/* CTA — 바닥 고정 */}
        <div style={{ marginTop: 'auto', paddingBottom: 'max(14px, env(safe-area-inset-bottom, 0px))', paddingTop: 16 }}>
          {giftError && <div style={{ color: 'var(--cs-alert)', fontSize: 12, textAlign: 'center', marginBottom: 8 }}>{giftError}</div>}
          <CSBtn variant="primary" size="lg" full disabled={giftBusy} onClick={next} iconRight={<CSIcon name="arrow" size={16} />}>
            {last
              ? (giftBusy ? '선물 받는 중…' : alreadyClaimed ? '뽑기 화면으로' : '받고 바로 뽑으러 가기')
              : '다음'}
          </CSBtn>
        </div>
      </div>
    </div>
  );
}

/* ── 스텝1 · 환영 (claude.ai/design 채택안 그대로) ── */
function StepWelcome({ nick }) {
  return (
    <>
      <div style={{ fontSize: 11.5, color: 'var(--cs-accent)', letterSpacing: '0.12em', fontWeight: 700, fontFamily: 'var(--cs-font-mono)' }}>환영</div>
      <h1 style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.25, marginTop: 8, fontFamily: 'var(--cs-font-serif)' }}>
        반가워요, {nick || '새 투자자'}님 <CSIcon name="coin" size={18} /><br /><span className="cs-italic">제가 황둥이예요.</span>
      </h1>
      <p style={{ fontSize: 14, color: 'var(--cs-ink-2)', lineHeight: 1.6, marginTop: 10, textWrap: 'pretty' }}>
        솟다는 <b>진짜 돈 없이</b> 투자를 연습하고,<br />잘 안 돼도 같이 복기하는 곳이에요.<br />제가 처음부터 끝까지 안내할게요.
      </p>
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 16 }}>
        <CastPortrait id="hwangdoongi" size={260} mood="happy" />
      </div>
    </>
  );
}

/* ── 스텝2 · 활동 BULL 환산 가시화 (핵심 아하) ── */
function StepBull() {
  const acts = [
    { label: '출석 체크', v: 1500 },
    { label: '머니타입 진단', v: 5000 },
    { label: '커뮤니티 글', v: 2000 },
  ];
  return (
    <>
      <div style={{ fontSize: 11.5, color: 'var(--cs-accent)', letterSpacing: '0.12em', fontWeight: 700, fontFamily: 'var(--cs-font-mono)' }}>활동 적립</div>
      {/* 신규유저는 아직 적립 전 → '벌써 쌓였어요'(거짓)가 아니라 미래형 예시로 (감사 P2) */}
      <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.3, marginTop: 8, fontFamily: 'var(--cs-font-serif)' }}>
        활동하면<br />이만큼 모을 수 있어요 <CSIcon name="sparkle" size={18} />
      </h1>
      {/* 카운터 */}
      <div style={{ marginTop: 22, padding: '24px 20px', background: 'var(--cs-surface)', borderRadius: 'var(--cs-r-xl)', textAlign: 'center', boxShadow: 'var(--cs-shadow-1)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 8 }}>
          <span className="num" style={{ fontSize: 44, fontWeight: 800, letterSpacing: '-0.04em', fontFamily: 'var(--cs-font-mono)', color: 'var(--cs-ink)' }}>
            <BullCounter target={8500} />
          </span>
          <span style={{ fontSize: 18, color: 'var(--cs-mute)', fontWeight: 600 }}>BULL</span>
        </div>
        {/* 활동칩 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--cs-line)' }}>
          {acts.map((a) => (
            <div key={a.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--cs-ink-2)' }}>{a.label}</span>
              <span className="num" style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--cs-accent)', fontFamily: 'var(--cs-font-mono)' }}>+{a.v.toLocaleString('ko-KR')}</span>
            </div>
          ))}
        </div>
      </div>
      <p style={{ fontSize: 13, color: 'var(--cs-mute)', lineHeight: 1.55, marginTop: 16, textAlign: 'center' }}>
        출석하고 글 쓰고 게임할수록 BULL이 모여요.
      </p>
    </>
  );
}

/* ── 스텝3 · 모의투자 제안 ── */
function StepSim() {
  return (
    <>
      <div style={{ fontSize: 11.5, color: 'var(--cs-accent)', letterSpacing: '0.12em', fontWeight: 700, fontFamily: 'var(--cs-font-mono)' }}>다음 단계</div>
      <h1 style={{ fontSize: 25, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.28, marginTop: 8, fontFamily: 'var(--cs-font-serif)' }}>
        이 BULL로<br /><span style={{ color: 'var(--cs-accent)' }}>주식 모의투자</span> 해볼까요?
      </h1>
      <p style={{ fontSize: 14, color: 'var(--cs-ink-2)', lineHeight: 1.6, marginTop: 10 }}>
        실전처럼 <b>실제 시세</b>로 굴려봐요.<br />가상자금이라 잃어도 진짜 돈은 안 나가요.
      </p>
      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 12 }}>
        <CastPortrait id="hwangdoongi" size={220} mood="happy" />
      </div>
    </>
  );
}

/* ── 스텝4 · 실패박물관 티저 ── */
function StepMuseum() {
  return (
    <>
      <div style={{ fontSize: 11.5, color: 'var(--cs-accent)', letterSpacing: '0.12em', fontWeight: 700, fontFamily: 'var(--cs-font-mono)' }}>걱정 마세요</div>
      <h1 style={{ fontSize: 25, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.28, marginTop: 8, fontFamily: 'var(--cs-font-serif)' }}>
        손실이 나도 괜찮아요<br />같이 <span className="cs-italic">복기</span>해드려요.
      </h1>
      <p style={{ fontSize: 14, color: 'var(--cs-ink-2)', lineHeight: 1.6, marginTop: 10 }}>
        잃은 건 못 되돌려도, 전시는 할 수 있어요.<br />황둥이가 매매 습관을 진단하고 처방해드려요.
      </p>
      {/* 골드-온-다크 실패박물관 미니 티저 */}
      <div style={{ marginTop: 20, padding: 18, borderRadius: 'var(--cs-r-xl)', color: 'var(--cs-on-ink)',
        background: `radial-gradient(120% 90% at 85% -10%, rgba(228,197,122,.14), transparent 55%), linear-gradient(160deg, ${FM_INK_DEEP} 0%, var(--cs-museum-ink2) 100%)`,
        boxShadow: 'var(--cs-shadow-hero)' }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.12em', color: 'rgba(228,197,122,.85)', fontFamily: 'var(--cs-font-mono)' }}>실패 박물관 · 鑑定</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <CastAvatar id="hwangdoongi" expr="focused" size={40} />
          <div style={{ fontSize: 13.5, lineHeight: 1.5, color: '#F4ECD8' }}>
            “회전문? 몰빵? 물타기?<br />유형을 짚어드릴게요.”
          </div>
        </div>
      </div>
      <div style={{ flex: 1 }} />
    </>
  );
}

/* ── 스텝5 · G10 첫 10연 선물 ──
   claimed=true → 이미 받은 계정(서버 grant_draw_ticket 이 already_granted 반환). 앱 재설치로 온보딩이
   다시 떠도 선물은 계정당 1회라 지급되지 않는다. 예전엔 이 사실을 화면이 몰라서 "드려요"를 그대로
   보여줬고, 효신님이 "매번 준다는데 안 준다"고 느낀 원인이었다(2026-07-26). 서버는 처음부터 맞았다. */
function StepGift({ claimed = false }: { claimed?: boolean }) {
  return (
    <>
      <div style={{ fontSize: 11.5, color: 'var(--cs-accent)', letterSpacing: '0.12em', fontWeight: 700, fontFamily: 'var(--cs-font-mono)' }}>첫 투자자 선물</div>
      <h1 style={{ fontSize: 25, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.28, marginTop: 8, fontFamily: 'var(--cs-font-serif)' }}>
        {claimed
          ? <>이 선물은<br /><span style={{ color: 'var(--cs-accent)' }}>이미 받으셨어요.</span></>
          : <>첫 카드 팀을<br /><span style={{ color: 'var(--cs-accent)' }}>10연으로 만나보세요.</span></>}
      </h1>
      <p style={{ fontSize: 14, color: 'var(--cs-ink-2)', lineHeight: 1.6, marginTop: 10 }}>
        {claimed
          ? <>첫 10연 뽑기권은 <b>계정당 한 번</b>이에요.<br />아직 안 쓰셨다면 뽑기 화면에 그대로 있어요.</>
          : <>계정당 한 번, 10연 뽑기권 1장을 드려요.<br />레어 이상 카드가 최소 1장 포함돼요.</>}
      </p>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 18 }}>
        <div style={{ width: 210, padding: '28px 24px', borderRadius: 'var(--cs-r-xl)', background: 'var(--cs-surface)', boxShadow: 'var(--cs-shadow-2)', textAlign: 'center', opacity: claimed ? 0.62 : 1 }}>
          <BullCoin size={76} />
          <div style={{ fontSize: 18, fontWeight: 800, marginTop: 12 }}>10연 뽑기권 × 1</div>
          <div style={{ fontSize: 11.5, color: 'var(--cs-mute)', marginTop: 5 }}>{claimed ? '지급 완료' : 'BULL 차감 없이 사용'}</div>
        </div>
      </div>
    </>
  );
}

/* ── BULL 카운터 (0 → target 롤업) ── */
function BullCounter({ target = 8500 }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let cur = 0;
    const inc = target / 28;
    const id = setInterval(() => {
      cur += inc;
      if (cur >= target) { cur = target; clearInterval(id); }
      setV(Math.round(cur));
    }, 30);
    return () => clearInterval(id);
  }, [target]);
  return <>{v.toLocaleString('ko-KR')}</>;
}

/* ── 진행 단계 인디케이터 ── */
function Step({ done, active }) {
  return (
    <div style={{ flex: 1, height: 3, borderRadius: 999, background: done || active ? 'var(--cs-accent)' : 'var(--cs-line-2)', opacity: active ? 0.45 : 1 }} />
  );
}
